import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from 'react-bootstrap';
import Layout from '../Components/Layout';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const NewRecordQueue = () => {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRecords, setSelectedRecords] = useState(new Set());
    const [pageNumber, setPageNumber] = useState(1);
    const [perPage] = useState(25);
    const searchTimerRef = useRef(null);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                skip: (pageNumber - 1) * perPage,
                limit: perPage
            };
            if (filter) params.entity_type = filter;
            if (debouncedSearch) params.search = debouncedSearch;

            const response = await axios.get('/api/new-records', { params });
            if (response.data.success) {
                setRecords(response.data.records);
                setTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching new records:', error);
            toast.error('Failed to load new records');
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch, pageNumber, perPage]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setPageNumber(1);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPageNumber(1);
        setSelectedRecords(new Set());
    };

    const handleSelectRecord = (entityType, entityId) => {
        const key = `${entityType}:${entityId}`;
        setSelectedRecords(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedRecords.size === records.length) {
            setSelectedRecords(new Set());
        } else {
            const allKeys = records.map(r => `${r.entity_type}:${r.entity_id}`);
            setSelectedRecords(new Set(allKeys));
        }
    };

    const handleMarkReviewed = async (entityType, entityId) => {
        try {
            await axios.put(`/api/new-records/${entityType}/${entityId}/review`);
            toast.success('Record marked as reviewed');
            fetchRecords();
            setSelectedRecords(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${entityType}:${entityId}`);
                return newSet;
            });
        } catch (error) {
            toast.error('Failed to mark as reviewed');
        }
    };

    const handleBatchReview = async () => {
        if (selectedRecords.size === 0) return;

        const items = Array.from(selectedRecords).map(key => {
            const [entity_type, entity_id] = key.split(':');
            return { entity_type, entity_id: parseInt(entity_id) };
        });

        try {
            const response = await axios.put('/api/new-records/batch-review', { records: items });
            if (response.data.success) {
                toast.success(`${response.data.reviewed_count} record(s) marked as reviewed`);
                setSelectedRecords(new Set());
                fetchRecords();
            }
        } catch (error) {
            toast.error('Failed to batch review');
        }
    };

    const navigateToRecord = (record) => {
        if (record.entity_type === 'rfq') {
            window.location.href = `/request-quote/edit?id=${record.entity_id}`;
        } else if (record.entity_type === 'raws') {
            window.location.href = `/view-raws/${record.entity_id}`;
        }
    };

    const totalPages = Math.ceil(total / perPage);

    return (
        <Layout>
            <ToastContainer position="top-right" autoClose={3000} />
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', paddingTop: '20px' }}>
                <section className="section pb-0" style={{ paddingTop: '20px', paddingBottom: '0px' }}>
                    <div className="container-fluid" style={{ paddingLeft: '24px' }}>

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h4 style={{ fontWeight: '700', marginBottom: '4px' }}>
                                    New Records
                                    {total > 0 && (
                                        <Badge bg="primary" style={{ marginLeft: '10px', fontSize: '14px' }}>
                                            {total}
                                        </Badge>
                                    )}
                                </h4>
                                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                                    Records pending downstream creation review
                                </p>
                            </div>
                            {selectedRecords.size > 0 && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleBatchReview}
                                    style={{ padding: '8px 16px' }}
                                >
                                    Mark Selected as Reviewed ({selectedRecords.size})
                                </button>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="d-flex mb-3" style={{ gap: '12px', alignItems: 'center' }}>
                            <div className="btn-group" role="group">
                                <button
                                    className={`btn btn-sm ${filter === '' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => handleFilterChange('')}
                                >
                                    All
                                </button>
                                <button
                                    className={`btn btn-sm ${filter === 'rfq' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => handleFilterChange('rfq')}
                                >
                                    RFQs
                                </button>
                                <button
                                    className={`btn btn-sm ${filter === 'raws' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => handleFilterChange('raws')}
                                >
                                    Parts
                                </button>
                            </div>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search by name..."
                                value={search}
                                onChange={handleSearchChange}
                                style={{ maxWidth: '300px', padding: '8px 12px' }}
                            />
                        </div>

                        {/* Table */}
                        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none', width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={records.length > 0 && selectedRecords.size === records.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none' }}>Type</th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none' }}>ID</th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none' }}>Name</th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none' }}>Company</th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none' }}>Created</th>
                                            <th style={{ padding: '16px 24px', fontWeight: '600', color: '#374151', border: 'none', textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <div className="d-flex justify-content-center align-items-center" style={{ gap: '10px' }}>
                                                        <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                                        <span>Loading records...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : records.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4 text-muted">
                                                    No new records pending review.
                                                </td>
                                            </tr>
                                        ) : (
                                            records.map((record, index) => {
                                                const key = `${record.entity_type}:${record.entity_id}`;
                                                const isSelected = selectedRecords.has(key);
                                                return (
                                                    <tr key={key} style={{ borderTop: index > 0 ? '1px solid #f3f4f6' : 'none' }}>
                                                        <td style={{ padding: '16px 24px', border: 'none' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectRecord(record.entity_type, record.entity_id)}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none' }}>
                                                            <Badge bg={record.entity_type === 'rfq' ? 'primary' : 'info'}>
                                                                {record.entity_type === 'rfq' ? 'RFQ' : 'Part'}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none', fontWeight: '600' }}>
                                                            <a
                                                                href="#"
                                                                onClick={(e) => { e.preventDefault(); navigateToRecord(record); }}
                                                                style={{ color: '#007bff', textDecoration: 'none' }}
                                                            >
                                                                {record.display_id}
                                                            </a>
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none' }}>
                                                            {record.name || '-'}
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none' }}>
                                                            {record.company || '-'}
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none' }}>
                                                            {record.created_at
                                                                ? new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                                : '-'}
                                                        </td>
                                                        <td style={{ padding: '16px 24px', border: 'none', textAlign: 'center' }}>
                                                            <button
                                                                className="btn btn-outline-success btn-sm"
                                                                onClick={() => handleMarkReviewed(record.entity_type, record.entity_id)}
                                                                style={{ fontSize: '13px' }}
                                                            >
                                                                Mark Reviewed
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                                            Showing {((pageNumber - 1) * perPage) + 1} to {Math.min(pageNumber * perPage, total)} of {total}
                                        </span>
                                        <div className="btn-group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                disabled={pageNumber <= 1}
                                                onClick={() => setPageNumber(p => p - 1)}
                                            >
                                                Previous
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                disabled={pageNumber >= totalPages}
                                                onClick={() => setPageNumber(p => p + 1)}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default NewRecordQueue;
